/*
 * Bundle (quantity) pricing in the cart.
 *
 * The bundle discounts live in the checkout's CRM (Settings → Quantity
 * pricing) and are applied by the checkout, not by Shopify, so Shopify's cart
 * only knows full prices. This script reads the same tiers from the checkout
 * API (GET {origin}/api/storefront/offers) and prices the cart the same way
 * the checkout does (packages/shared/src/pricing.ts):
 *   - units of one product are counted together across its variants;
 *   - the best tier whose quantity is <= those units applies;
 *   - percent tiers: discount = round(subtotal * pct / 100);
 *     bundle-price tiers: total = round(price * units / tierQuantity);
 *   - the product's discount is split across its lines (largest remainder).
 * It then rewrites the prices on the cart page (sections/main-cart.liquid)
 * and in the cart popup (assets/scaled-bypass.js → renderCart).
 */
(function () {
  'use strict';

  var script = document.querySelector('script[src*="external-checkout.js"]');
  var origin = ((script && script.getAttribute('data-origin')) || '').replace(/\/+$/, '');
  var cache = {};

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function loadOffers(productIds) {
    var ids = productIds.filter(function (id) { return !(id in cache); });
    if (!origin || !ids.length) return Promise.resolve(cache);
    return fetch(origin + '/api/storefront/offers?products=' + ids.join(','), { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : { offers: {} }; })
      .then(function (body) {
        ids.forEach(function (id) {
          var o = body && body.offers && body.offers[id];
          cache[id] = o && o.tiers ? o.tiers : [];
        });
        return cache;
      })
      .catch(function () { return cache; });
  }

  function tierFor(tiers, units) {
    var best = null;
    (tiers || []).forEach(function (t) {
      if (t.quantity <= units && (!best || t.quantity > best.quantity)) best = t;
    });
    return best;
  }

  function tierTotal(tier, units, subtotal) {
    var total;
    if (tier.kind === 'percent') {
      var pct = Math.min(100, Math.max(0, tier.value));
      total = subtotal - Math.round((subtotal * pct) / 100);
    } else {
      total = Math.round((tier.value * units) / tier.quantity);
    }
    return Math.max(0, Math.min(subtotal, total));
  }

  function split(amount, weights) {
    var sum = weights.reduce(function (a, b) { return a + b; }, 0);
    if (!sum) return weights.map(function () { return 0; });
    var raw = weights.map(function (w) { return (amount * w) / sum; });
    var parts = raw.map(Math.floor);
    var left = amount - parts.reduce(function (a, b) { return a + b; }, 0);
    raw
      .map(function (r, i) { return { i: i, frac: r - Math.floor(r) }; })
      .sort(function (a, b) { return b.frac - a.frac || a.i - b.i; })
      .slice(0, left)
      .forEach(function (x) { parts[x.i] += 1; });
    return parts;
  }

  /** { total: cents off the cart, lines: { [item.key]: cents off that line } } */
  function price(cart) {
    var items = (cart && cart.items) || [];
    var ids = [];
    items.forEach(function (it) { if (ids.indexOf(String(it.product_id)) === -1) ids.push(String(it.product_id)); });
    return loadOffers(ids).then(function (offers) {
      var result = { total: 0, lines: {} };
      ids.forEach(function (pid) {
        var group = items.filter(function (it) { return String(it.product_id) === pid; });
        var units = group.reduce(function (s, it) { return s + it.quantity; }, 0);
        var tier = tierFor(offers[pid], units);
        if (!tier) return;
        var values = group.map(function (it) { return it.price * it.quantity; });
        var subtotal = values.reduce(function (a, b) { return a + b; }, 0);
        var discount = subtotal - tierTotal(tier, units, subtotal);
        if (discount <= 0) return;
        split(discount, values).forEach(function (d, i) { result.lines[group[i].key] = d; });
        result.total += discount;
      });
      return result;
    });
  }

  function shopifySavings(cart) {
    var compare = 0;
    (cart.items || []).forEach(function (it) {
      var cmp = parseInt(it.compare_at_price, 10) || 0;
      if (cmp > it.final_price) compare += (cmp - it.final_price) * it.quantity;
    });
    return (parseInt(cart.total_discount, 10) || 0) + compare;
  }

  function allocated(item) {
    return (item.discount_allocations || []).reduce(function (s, a) { return s + (parseInt(a.amount, 10) || 0); }, 0);
  }

  /* ── Cart page ─────────────────────────────────────────────────────────── */
  function applyToCartPage(cart) {
    if (!document.getElementById('cart-total')) return Promise.resolve();
    return price(cart).then(function (off) {
      var cur = cart.currency;
      (cart.items || []).forEach(function (item) {
        var d = off.lines[item.key] || 0;
        var priceEl = document.getElementById('cart-item-price-' + item.key.replace(/:/g, '-'));
        if (!priceEl) return;
        var finalLine = Math.max(0, item.final_line_price - allocated(item) - d);
        priceEl.textContent = money(finalLine, cur);
        if (!d) return;
        var prev = priceEl.previousElementSibling;
        if (!prev || !prev.classList.contains('cart-item__price--orig')) {
          prev = document.createElement('p');
          prev.className = 'cart-item__price--orig';
          priceEl.parentNode.insertBefore(prev, priceEl);
        }
        prev.textContent = money(item.original_line_price, cur);
        var save = priceEl.nextElementSibling;
        if (!save || !save.classList.contains('cart-item__saving')) {
          save = document.createElement('p');
          save.className = 'cart-item__saving';
          priceEl.parentNode.insertBefore(save, priceEl.nextSibling);
        }
        save.textContent = '(You save ' + money(item.original_line_price - finalLine, cur) + ')';
      });
      var savings = shopifySavings(cart) + off.total;
      var row = document.getElementById('cart-savings-row');
      var amt = document.getElementById('cart-savings');
      if (row && amt) {
        row.style.display = savings > 0 ? '' : 'none';
        amt.textContent = savings > 0 ? '-' + money(savings, cur) : '';
      }
      document.getElementById('cart-total').textContent = money(Math.max(0, cart.total_price - off.total), cur);
    });
  }

  /* ── Cart popup (called from scaled-bypass.js renderCart) ──────────────── */
  function applyToPopup(cart) {
    return price(cart).then(function (off) {
      if (!off.total) return;
      var cur = cart.currency;
      (cart.items || []).forEach(function (item) {
        var el = document.querySelector('.kb-cart-item__price[data-line-key="' + item.key + '"]');
        var d = off.lines[item.key] || 0;
        if (el && d) {
          el.innerHTML =
            '<s class="kb-cart-item__price-was">' + money(item.final_line_price, cur) + '</s> ' +
            money(Math.max(0, item.final_line_price - d), cur);
        }
      });
      var totalEl = document.getElementById('kb-cart-total');
      if (totalEl) totalEl.textContent = money(Math.max(0, cart.total_price - off.total), cur);
      var row = document.getElementById('kb-cart-savings-row');
      var amt = document.getElementById('kb-cart-savings');
      if (row && amt) {
        amt.textContent = '-' + money(shopifySavings(cart) + off.total, cur);
        row.style.display = 'flex';
      }
    });
  }

  function refreshCartPage() {
    if (!document.getElementById('cart-total')) return;
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(applyToCartPage)
      .catch(function () {});
  }

  // The cart page rewrites its prices after every quantity change; re-apply
  // the bundle prices once its own update has landed.
  var nativeFetch = window.fetch;
  window.fetch = function (input) {
    var p = nativeFetch.apply(this, arguments);
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    if (/\/cart\/(change|update|add|clear)(\.js)?/.test(url)) {
      p.then(function () { setTimeout(refreshCartPage, 250); }, function () {});
    }
    return p;
  };

  window.KBOffers = { price: price, applyToPopup: applyToPopup, applyToCartPage: applyToCartPage };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshCartPage);
  else refreshCartPage();
})();
